import React from 'react';
import Header from './common/Header';
import Footer from './common/Footer';
import usePageContent from '../hooks/usePageContent';
import { formatPageContent } from '../utils/formatPageContent';

/**
 * TermsOfUsePage - Displays terms of use content from API or fallback
 */
const TermsOfUsePage = () => {
  const { page, loading, error } = usePageContent('terms-of-use');

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen text-white bg-gray-900">
        <Header />
        <main className="container max-w-4xl px-4 py-12 mx-auto">
          <div className="text-center mb-12">
            <div className="h-12 w-64 mx-auto bg-gray-700 rounded animate-pulse mb-4"></div>
            <div className="h-4 w-48 mx-auto bg-gray-700 rounded animate-pulse"></div>
          </div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-700 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-700 rounded animate-pulse w-5/6"></div>
            <div className="h-4 bg-gray-700 rounded animate-pulse w-4/6"></div>
            <div className="h-32 bg-gray-800 rounded-lg animate-pulse mt-8"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // If we have API content, render it
  if (page && (page.body || page.html)) {
    const content = formatPageContent(page.body || page.html);
    const title = page.page_name || page.nav_name || 'Terms of Use';
    const updatedAt = page.updated_at
      ? new Date(page.updated_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : null;

    return (
      <div className="min-h-screen text-white bg-gray-900">
        <Header />
        <main className="container max-w-4xl px-4 py-12 mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] bg-clip-text text-transparent">
              {title}
            </h1>
            {updatedAt && <p className="text-gray-400">Last updated: {updatedAt}</p>}
          </div>

          {/* Dynamic Content */}
          <div
            className="prose prose-lg prose-invert max-w-none dynamic-page-content"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </main>
        <Footer />
      </div>
    );
  }

  // Fallback to static content
  return (
    <div className="min-h-screen text-white bg-gray-900">
      <Header />

      <main className="container max-w-4xl px-4 py-12 mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] bg-clip-text text-transparent">
            Terms of Use
          </h1>
          <p className="text-gray-400">Last updated: February 23, 2024</p>
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-invert max-w-none">
          {/* Introduction */}
          <section className="mb-12">
            <div className="p-8 mb-8 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="mb-4 leading-relaxed text-gray-300">
                Please carefully read these terms and conditions before using this website (the
                "Site"). By accessing or using the Site, you ("user") agree to be legally bound by
                these terms and conditions. If you disagree with any of the following terms or
                conditions, please do not use this site.
              </p>
              <p className="leading-relaxed text-gray-300">
                Dotstudioz INC owns ElectricNOW and operates this Site. ElectricNOW reserves the
                right to change any terms or conditions without notice, effective upon their posting.
              </p>
            </div>
          </section>

          {/* Ownership of Intellectual Property Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">
              Ownership of Intellectual Property Rights
            </h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="mb-4 leading-relaxed text-gray-300">
                All text, photographs, images, illustrations, artwork, audio and video clips, design,
                software, graphic material, trademarks, service marks and trade names, and all
                intellectual property rights in and to such items (hereinafter "Content"), including
                any modifications, variation and/or derivative works thereof, constitute the sole and
                exclusive property of ElectricNOW or its subsidiaries, affiliates, licensors and
                content providers.
              </p>
              <p className="leading-relaxed text-gray-300">
                ElectricNOW is a trademark of Dotstudioz INC. All rights reserved. All other
                trademarks and service marks are the property of their respective owners.
              </p>
            </div>
          </section>

          {/* Limitation of Liability and Disclaimers */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">
              Limitation of Liability and Disclaimers
            </h2>
            <div className="p-6 border rounded-lg bg-red-900/20 border-red-500/30">
              <p className="mb-4 font-semibold leading-relaxed text-gray-300">
                USER EXPRESSLY AGREES THAT USE OF THE SITE IS AT USER'S SOLE RISK.
              </p>
              <p className="leading-relaxed text-gray-300">
                NEITHER ElectricNOW, ITS PARENTS, AFFILIATES, EMPLOYEES, AGENTS, SERVICE PROVIDERS,
                THIRD PARTY INFORMATION PROVIDERS, LICENSORS OR THE LIKE, WARRANT THAT THE SITE WILL
                BE UNINTERRUPTED OR ERROR FREE; NOR DO THEY MAKE ANY WARRANTY AS TO THE RESULTS THAT
                MAY BE OBTAINED FROM THE USE OF THE SITE, OR AS TO THE ACCURACY, RELIABILITY OR
                CONTENT OF ANY INFORMATION OR SERVICE CONTAINED IN OR PROVIDED THROUGH THE SITE.
              </p>
            </div>
          </section>

          {/* Third Party Links and Sites */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">Third Party Links and Sites</h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="leading-relaxed text-gray-300">
                This site may contain links to other Internet sites, resources and/or sponsors of the
                Site. ElectricNOW does not verify, warrant, endorse, or take responsibility for the
                availability, accuracy, completeness or quality of the content contained in these
                outside sites. Providing links to outside sites does not constitute ElectricNOW's
                approval of the content, policies or practices of those other sites. Be sure to
                review the terms of use and privacy policies posted on the outside sites after
                linking to them.
              </p>
            </div>
          </section>

          {/* User Submissions */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">User Submissions</h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="mb-4 leading-relaxed text-gray-300">
                By posting or submitting material to this page ("Submission"), you grant to
                ElectricNOW a perpetual, non-exclusive, irrevocable, fully-paid and royalty-free
                worldwide right and license in any and all media now known or later developed, to
                use, publish, reproduce, display, modify, transmit digitally, create derivative works
                based upon and otherwise exploit such Submission (including, without limitation, any
                names, voices, likenesses, performances and actions contained therein) for any
                purpose whatsoever (including, without limitation, advertising, commercial,
                promotional and publicity purposes) in ElectricNOW's discretion without additional
                notice, attribution or consideration to you or to any other person or entity.
              </p>
              <p className="leading-relaxed text-gray-300">
                Additionally, you certify that you are the original author or creator of your
                Submission (except as expressly indicated otherwise), that neither the permission nor
                consent of any other person or entity is required to enable ElectricNOW to legally
                use your Submission in accordance with this license, and that ElectricNOW's use of
                your Submission in accordance with this license will not violate or infringe upon the
                rights of any person or entity.
              </p>
            </div>
          </section>

          {/* Indemnification */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">Indemnification</h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="leading-relaxed text-gray-300">
                User agrees to defend, indemnify and hold ElectricNOW, its employees, agents,
                representatives, service providers, partners, affiliates, officers, directors,
                members, attorneys, shareholders and licensors harmless from any and all claims,
                liabilities, costs and expenses, including reasonable attorney's fees, arising in any
                way from user's use of the Site or user's breach of these terms of use.
              </p>
            </div>
          </section>

          {/* General Provisions */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">General Provisions</h2>
            <div className="p-6 bg-gray-800 border border-gray-700 rounded-lg">
              <p className="leading-relaxed text-gray-300">
                These terms and conditions shall be governed by and construed in accordance with the
                laws of the State of California, without regard to conflict of law provisions. Any
                dispute arising from use of the Site shall be resolved exclusively in the state or
                federal courts of Los Angeles County, California, and you submit to personal
                jurisdiction in those courts.
              </p>
            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6 text-[#9ec6f0]">Contact Us</h2>
            <div className="p-6 border rounded-lg bg-gradient-to-r from-gray-800 to-gray-700 border-[#2f6eb0]/30">
              <p className="leading-relaxed text-gray-300">
                If you have any questions or concerns about these Terms of Use, please contact us at:
              </p>
              <div className="mt-4 space-y-2">
                <p className="text-gray-300">
                  <strong className="text-[#9ec6f0]">Email:</strong>{' '}
                  <a
                    href="mailto:support@electricnow.tv"
                    className="text-[#9ec6f0] hover:text-[#b3cfed]"
                  >
                    support@electricnow.tv
                  </a>
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfUsePage;
